/**
 * SignatureList - Displays list of signatures with management actions
 */

import type { SignaturePublic } from '@garden-platform/shared';
import type * as React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useDeleteSignature, useSignatures } from '../../hooks';
import { SignatureCard } from './SignatureCard';
import { SignatureUploadDialog } from './SignatureUploadDialog';

export interface SignatureListProps {
  className?: string;
}

export const SignatureList: React.FC<SignatureListProps> = ({ className }) => {
  const [showInactive, setShowInactive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSignature, setEditingSignature] = useState<SignaturePublic | undefined>();

  const { data: signatures, isLoading } = useSignatures(showInactive);
  const deleteMutation = useDeleteSignature();

  const handleEdit = (signature: SignaturePublic) => {
    setEditingSignature(signature);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingSignature(undefined);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this signature?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingSignature(undefined);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading signatures...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Signatures</CardTitle>
              <CardDescription>Manage signature images for use on cheques</CardDescription>
            </div>
            <Button onClick={handleAdd}>Upload Signature</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showInactiveSignatures"
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            <Label htmlFor="showInactiveSignatures" className="text-sm font-normal">
              Show inactive signatures
            </Label>
          </div>

          {!signatures || signatures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No signatures found</p>
              <Button variant="outline" onClick={handleAdd}>
                Upload your first signature
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {signatures.map((signature) => (
                <SignatureCard
                  key={signature.id}
                  signature={signature}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SignatureUploadDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        signature={editingSignature}
      />
    </>
  );
};

SignatureList.displayName = 'SignatureList';
